import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import type { ReactNode } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Grabber } from '../components/Grabber'
import { ScrollFade, useScrollEdgeFades } from '../components/ScrollFade'
import { useNestedSheetHeight } from '../hooks/useNestedSheetHeight'
import { ChainAvatar } from '../icons/ChainAvatar'
import { FigmaCheckIcon, FigmaChevronLeftIcon } from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import type { AvailableChain } from '../transfer/availableChains'

interface ChainListProps {
  chains: AvailableChain[]
  /** Marks a row selected: `background/secondary` fill + a trailing check. */
  selectedChain?: AvailableChain
  onSelectChain?: (chain: AvailableChain) => void
  loading?: boolean
  skeletonCount?: number
  emptyLabel?: string
}

/**
 * The "Deposit crypto" chain picker — the first-run variant, shown before the
 * user has chosen a deposit network.
 */
export interface SelectChainScreenProps extends ChainListProps {
  onBack?: () => void
}

export function SelectChainScreen(props: SelectChainScreenProps) {
  const { t } = useFunkitTranslation()
  return (
    <DepositOptionPicker
      {...props}
      title={t('selectChain.title')}
      subtitle={t('selectChain.subtitle')}
    />
  )
}

function DepositOptionPicker({
  title,
  subtitle,
  ...props
}: SelectChainScreenProps & { title: string; subtitle: string }) {
  const { t } = useFunkitTranslation()
  const { onBack, ...chainList } = props
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const sheetHeight = useNestedSheetHeight()

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{
        height: sheetHeight,
        backgroundColor: theme.background.modal,
        paddingBottom: bottomInset(insets.bottom),
      }}
    >
      <Box style={styles.backTopBar}>
        <Grabber />
        <Pressable
          onPress={onBack}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
          style={styles.backPress}
        >
          <Box
            alignItems="center"
            justifyContent="center"
            style={[
              styles.backButton,
              {
                backgroundColor: theme.background.secondary,
                borderColor: theme.border.elements.default.color,
              },
            ]}
          >
            <FigmaChevronLeftIcon
              size={24}
              color={theme.content.button.icon.default}
            />
          </Box>
        </Pressable>
      </Box>

      <ChainList
        {...chainList}
        header={
          <Box style={styles.titleBlock}>
            <Text
              variant="bodyMedium"
              style={[
                styles.titleText,
                { color: theme.content.elements.primary },
              ]}
            >
              {title}
            </Text>
            <Text
              variant="body"
              style={{ color: theme.content.elements.secondary }}
            >
              {subtitle}
            </Text>
          </Box>
        }
      />
    </Box>
  )
}

/**
 * The "Select a chain" picker — the same list reopened from Transfer to change
 * networks. Cancel dismisses; the current chain is checked.
 */
export interface ChangeChainScreenProps extends ChainListProps {
  onCancel?: () => void
}

export function ChangeChainScreen(props: ChangeChainScreenProps) {
  const { t } = useFunkitTranslation()
  const { onCancel, ...chainList } = props
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const sheetHeight = useNestedSheetHeight()
  const cancelLabel = t('common.cancel')

  return (
    <Box
      borderTopLeftRadius="modal"
      borderTopRightRadius="modal"
      style={{
        height: sheetHeight,
        backgroundColor: theme.background.modal,
        paddingBottom: bottomInset(insets.bottom),
      }}
    >
      <Box alignItems="center" style={styles.titleTopBar}>
        <Grabber />
        <Text
          variant="bodyMedium"
          style={{ color: theme.content.elements.primary }}
        >
          {t('changeChain.title')}
        </Text>
      </Box>

      <ChainList {...chainList} />

      <Box style={styles.buttonContainer}>
        <Pressable
          onPress={onCancel}
          accessibilityRole="button"
          accessibilityLabel={cancelLabel}
        >
          <Box
            alignItems="center"
            justifyContent="center"
            borderRadius="button"
            style={[
              styles.cancelButton,
              { backgroundColor: theme.background.button.primary.default },
            ]}
          >
            <Text
              variant="bodyMedium"
              style={{ color: theme.content.button.primary.default }}
            >
              {cancelLabel}
            </Text>
          </Box>
        </Pressable>
      </Box>
    </Box>
  )
}

/** The scrollable chain rows shared by both pickers. */
function ChainList({
  chains,
  selectedChain,
  onSelectChain,
  loading = false,
  skeletonCount = 8,
  header,
  emptyLabel,
}: ChainListProps & { header?: ReactNode }) {
  const theme = useTheme()
  const { showTopFade, showBottomFade, scrollProps } = useScrollEdgeFades()

  return (
    <View style={styles.scrollArea}>
      <BottomSheetScrollView
        style={styles.list}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        {...scrollProps}
      >
        {header}
        {!loading && chains.length === 0 && emptyLabel ? (
          <Text variant="body" style={styles.emptyLabel}>
            {emptyLabel}
          </Text>
        ) : null}
        {loading
          ? Array.from({ length: skeletonCount }, (_, index) => (
              <ChainRowSkeleton key={index} />
            ))
          : chains.map((chain) => (
              <ChainRow
                key={chain.id}
                chain={chain}
                selected={chain.id === selectedChain?.id}
                onPress={onSelectChain}
              />
            ))}
      </BottomSheetScrollView>
      {showTopFade ? (
        <ScrollFade edge="top" color={theme.background.modal} id="chain-list" />
      ) : null}
      {showBottomFade ? (
        <ScrollFade
          edge="bottom"
          color={theme.background.modal}
          id="chain-list"
        />
      ) : null}
    </View>
  )
}

function ChainRow({
  chain,
  selected,
  onPress,
}: {
  chain: AvailableChain
  selected: boolean
  onPress?: (chain: AvailableChain) => void
}) {
  const theme = useTheme()
  return (
    <Pressable
      onPress={() => onPress?.(chain)}
      accessibilityRole="button"
      accessibilityLabel={chain.name}
      accessibilityState={{ selected }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        borderRadius="element"
        style={[
          styles.row,
          selected ? { backgroundColor: theme.background.secondary } : null,
        ]}
      >
        <ChainAvatar uri={chain.iconUri} />
        <Box flex={1}>
          <Text
            variant="bodyMedium"
            numberOfLines={1}
            style={{ color: theme.content.elements.primary }}
          >
            {chain.name}
          </Text>
        </Box>
        {selected ? (
          <FigmaCheckIcon size={20} color={theme.content.elements.secondary} />
        ) : null}
      </Box>
    </Pressable>
  )
}

function ChainRowSkeleton() {
  const theme = useTheme()
  return (
    <Box flexDirection="row" alignItems="center" style={styles.row}>
      <Box
        style={[
          styles.skeletonIcon,
          { backgroundColor: theme.background.pill.pressed },
        ]}
      />
      <Box flex={1}>
        <Box
          style={[
            styles.skeletonName,
            { backgroundColor: theme.background.pill.pressed },
          ]}
        />
      </Box>
    </Box>
  )
}

// Devices without a home indicator report a 0 bottom inset, which would leave
// the cancel button flush against the screen edge.
const MIN_BOTTOM_INSET = 16
const bottomInset = (inset: number) => Math.max(inset, MIN_BOTTOM_INSET)

const styles = StyleSheet.create({
  backTopBar: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12 },
  backPress: { alignSelf: 'flex-start' },
  // A white fill on the white sheet reads as nothing on device, so the circle
  // takes the secondary surface + hairline.
  backButton: {
    width: 48,
    height: 48,
    borderRadius: 999,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 2,
  },
  // Same geometry as the Add money sheet.
  titleTopBar: { height: 64, paddingTop: 25, paddingBottom: 12 },
  // Inside the list's 16 inset, so 12 more lines it up with the row text.
  titleBlock: {
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 2,
  },
  // No theme variant for 21/25; the medium cut rides on `bodyMedium` because an
  // inline fontWeight can't select a custom font's cut in RN/Expo.
  titleText: { fontSize: 21, lineHeight: 25 },
  scrollArea: { flex: 1, position: 'relative' },
  list: { flex: 1, width: '100%' },
  // The trailing buffer keeps the last row clear of the sheet edge.
  listContent: { paddingHorizontal: 16, paddingBottom: 16 },
  emptyLabel: { paddingHorizontal: 12, paddingVertical: 20 },
  row: { gap: 12, paddingHorizontal: 12, paddingVertical: 10 },
  buttonContainer: { paddingHorizontal: 20, paddingTop: 8 },
  cancelButton: { height: 56, paddingHorizontal: 24 },
  skeletonIcon: { width: 36, height: 36, borderRadius: 999 },
  skeletonName: { width: 157, height: 16, borderRadius: 4 },
})
